import React, { useEffect, useMemo, useRef, useState } from 'react';
import WordCloud from 'react-wordcloud';

// Very lightweight stopwords list. You can expand or localize as needed.
const DEFAULT_STOPWORDS = new Set([
  'i','me','my','myself','we','our','ours','ourselves','you','your','yours','yourself','yourselves',
  'he','him','his','himself','she','her','hers','herself','it','its','itself','they','them','their','theirs','themselves',
  'what','which','who','whom','this','that','these','those','am','is','are','was','were','be','been','being',
  'have','has','had','having','do','does','did','doing','a','an','the','and','but','if','or','because','as','until','while',
  'of','at','by','for','with','about','against','between','into','through','during','before','after','above','below',
  'to','from','up','down','in','out','on','off','over','under','again','further','then','once','here','there',
  'when','where','why','how','all','any','both','each','few','more','most','other','some','such','no','nor','not','only',
  'own','same','so','than','too','very','s','t','can','will','just','don','should','now','could','would','also',
  // CIIS specific noise words
  'ciis','2021'
]);

function normalizeToken(token) {
  // strip punctuation, numbers, and underscores, keep letters
  const t = token
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, '') // urls
    .replace(/[^a-zA-Z\-']/g, ' ') // keep hyphenated words
    .trim();
  return t;
}

function tokenize(text, stopwords) {
  if (!text) return [];
  const norm = normalizeToken(text);
  return norm
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => w.length > 2) // min length
    .filter(w => !stopwords.has(w));
}

function buildFrequencies(texts, stopwords) {
  const freq = new Map();
  texts.forEach(t => {
    tokenize(t, stopwords).forEach(tok => {
      freq.set(tok, (freq.get(tok) || 0) + 1);
    });
  });
  return Array.from(freq.entries())
    .map(([text, value]) => ({ text, value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Props:
 * - listId: HubSpot list ID (number or string)
 * - property: HubSpot contact property to read remarks from
 * - fetchUrl: optional custom API to fetch remarks array from, default `/api/hubspot/remarks?listId=...&property=...`
 * - stopwords: optional Set of words to exclude
 * - maxWords: optional number to cap words in cloud
 */
const WordCloudView = ({ listId, property, fetchUrl, stopwords, maxWords = 120 }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remarks, setRemarks] = useState([]);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const url = useMemo(() => {
    if (fetchUrl) return fetchUrl;
    const params = new URLSearchParams({ listId: String(listId || ''), property: String(property || '') });
    return `/api/hubspot/remarks?${params.toString()}`;
  }, [fetchUrl, listId, property]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        // Expecting { remarks: string[] }
        const arr = Array.isArray(data) ? data : Array.isArray(data?.remarks) ? data.remarks : [];
        if (alive) setRemarks(arr.filter(Boolean));
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load remarks');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [url]);

  const words = useMemo(() => {
    const sw = stopwords || DEFAULT_STOPWORDS;
    const freq = buildFrequencies(remarks, sw);
    return freq.slice(0, maxWords);
  }, [remarks, stopwords, maxWords]);

  // Observe container width to size the word cloud responsively
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    // Initialize width
    setContainerWidth(node.clientWidth || 0);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(entries => {
        for (const entry of entries) {
          const w = entry.contentRect?.width || node.clientWidth || 0;
          setContainerWidth(w);
        }
      });
      ro.observe(node);
    } else {
      const onResize = () => setContainerWidth(node.clientWidth || 0);
      window.addEventListener('resize', onResize);
      onResize();
      return () => window.removeEventListener('resize', onResize);
    }
    return () => {
      try { ro && ro.disconnect(); } catch (e) {}
    };
  }, []);

  if (loading) return <div>Loading word cloud…</div>;
  if (error) return <div style={{ color: 'red' }}>Word cloud error: {error}</div>;
  if (!words.length) return <div>No data available for the word cloud.</div>;

  const options = {
    rotations: 2,
    rotationAngles: [-45, 0],
    fontFamily: 'sans-serif',
    fontSizes: [14, 64],
    deterministic: true
  };

  // Compute size: full container width (fallback to viewport on first render)
  const fallbackW = typeof window !== 'undefined' ? window.innerWidth : 800;
  const width = Math.floor((containerWidth && containerWidth > 0) ? containerWidth : fallbackW);
  const height = Math.floor(width * 0.6); // adjust aspect ratio as needed
  const size = [width, height];

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <WordCloud words={words} options={options} size={size} />
    </div>
  );
};

export default WordCloudView;
